import type { ColocaStatusPillProps } from "@/shared/components/data-display/ColocaStatusPill";

import type {
  OrganizationWebhookConfig,
  OrganizationWebhookDeliveriesQuery,
  OrganizationWebhookDeliveryStatus,
  OrganizationWebhookEvent,
  OrganizationWebhookEventCatalogEntry,
  OrganizationWebhookEventType,
} from "../services/organizationWebhooksService";

/** Pure helpers of the webhooks section — kept out of the components so they run in the `unit` project. */

export type WebhookEndpointState = "active" | "paused" | "auto_disabled";

/**
 * What the endpoint is doing right now. `disabled_at` only ever comes from the backend's auto-disable policy,
 * so `is_active: false` with it set is "Coloca stopped this", without it "you paused this" — two different
 * things to tell the reader, even though the switch reads the same.
 */
export function endpointState(
  config: Pick<OrganizationWebhookConfig, "is_active" | "disabled_at">,
): WebhookEndpointState {
  if (config.is_active) return "active";
  return config.disabled_at ? "auto_disabled" : "paused";
}

export function deliveryStatusVariant(
  status: OrganizationWebhookDeliveryStatus,
): NonNullable<ColocaStatusPillProps["variant"]> {
  switch (status) {
    case "delivered":
      return "success";
    case "pending":
      return "info";
    case "failed":
    case "exhausted":
      return "danger";
  }
}

/**
 * Mirrors the backend allowlist. `pending` is the only status that cannot be re-queued, and not as policy: it
 * still has a live job, so re-queuing would race two attempts on one delivery.
 */
export function isRetryableDelivery(status: OrganizationWebhookDeliveryStatus): boolean {
  return status === "failed" || status === "exhausted" || status === "delivered";
}

/**
 * Whether re-queuing this delivery is a **re-send** rather than a retry — i.e. it already arrived.
 *
 * The two are worth naming differently on screen. Retrying an undelivered event resumes the same delivery;
 * re-sending a delivered one creates a new one with a new envelope id, so a receiver deduplicating on that id
 * will process the event a second time. That is the intent of a re-send, but it is a consequence the reader
 * should see before clicking.
 */
export function isResendDelivery(status: OrganizationWebhookDeliveryStatus): boolean {
  return status === "delivered";
}

/** 1-indexed page → `skip`. */
export function pageToSkip(page: number, perPage: number): number {
  return Math.max(0, page - 1) * perPage;
}

/**
 * The date-range field yields `YYYY-MM-DD` days, the backend validates `@IsDateString` instants and compares
 * against `created_at`. Widen each day to its UTC boundaries so a range of "today – today" still covers the
 * whole day; an unset side stays unset.
 */
export function dayRangeToInstants(from: string, to: string): { from?: string; to?: string } {
  return {
    from: from ? `${from}T00:00:00.000Z` : undefined,
    to: to ? `${to}T23:59:59.999Z` : undefined,
  };
}

export interface DeliveriesFilterState {
  status: OrganizationWebhookDeliveryStatus | "all";
  type: OrganizationWebhookEventType | "all";
  event: OrganizationWebhookEvent | "all";
  /** Digits as typed; `""` when unset. */
  status_code: string;
  from: string;
  to: string;
}

export const DEFAULT_DELIVERIES_FILTERS: DeliveriesFilterState = {
  status: "all",
  type: "all",
  event: "all",
  status_code: "",
  from: "",
  to: "",
};

export function hasActiveDeliveriesFilters(filters: DeliveriesFilterState): boolean {
  return (
    filters.status !== "all" ||
    filters.type !== "all" ||
    filters.event !== "all" ||
    filters.status_code !== "" ||
    filters.from !== "" ||
    filters.to !== ""
  );
}

/**
 * The events the event filter offers under the chosen type: that group's events, or every event (in catalog
 * order, deduplicated) while the type is "all". An unknown type yields none rather than every event.
 */
export function eventsForType(
  catalog: readonly OrganizationWebhookEventCatalogEntry[],
  type: string,
): OrganizationWebhookEvent[] {
  if (type === "all") return Array.from(new Set(catalog.flatMap((entry) => entry.events)));
  return [...(catalog.find((entry) => entry.type === type)?.events ?? [])];
}

/**
 * Whether the draft's event still belongs to the chosen type. An event outside it is a pair the backend never
 * produces, so the filter resets it instead of sending a query that can only come back empty. "all" on either
 * side is always compatible, and so is a type the catalog does not list (nothing to check it against —
 * typically the catalog has not loaded yet).
 */
export function isEventCompatibleWithType(
  catalog: readonly OrganizationWebhookEventCatalogEntry[],
  type: string,
  event: string,
): boolean {
  if (type === "all" || event === "all") return true;
  const allowed = catalog.find((entry) => entry.type === type)?.events;
  if (!allowed) return true;
  // `some` rather than `includes`: `event` is a plain string, and comparing it against the union's members is
  // what tells us whether it is still one of them.
  return allowed.some((candidate) => candidate === event);
}

/** Filter state → the query the service sends. `"all"` and blanks are dropped; the code becomes a number. */
export function buildDeliveriesQuery(
  filters: DeliveriesFilterState,
  page: number,
  perPage: number,
): OrganizationWebhookDeliveriesQuery {
  const code = filters.status_code.trim();
  const parsedCode = /^\d{3}$/.test(code) ? Number(code) : undefined;
  return {
    skip: pageToSkip(page, perPage),
    limit: perPage,
    status: filters.status === "all" ? undefined : filters.status,
    type: filters.type === "all" ? undefined : filters.type,
    event: filters.event === "all" ? undefined : filters.event,
    status_code: parsedCode,
    ...dayRangeToInstants(filters.from, filters.to),
  };
}

/** `143` → `"143 ms"`, `2400` → `"2.4 s"`; null when there was no attempt yet. */
export function formatLatency(ms: number | null | undefined): string | null {
  if (ms == null) return null;
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(1).replace(/\.0$/, "")} s`;
}

/**
 * Pretty-prints whatever the receiver or the envelope carried. A string that parses as JSON is re-indented;
 * anything else is shown verbatim so a plain-text `OK` or an HTML error page stays readable.
 */
export function prettyJson(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") {
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }
  return JSON.stringify(value, null, 2);
}
